import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, CircularProgress, Grid, MenuItem, IconButton,
  Tooltip, InputAdornment, Avatar,
} from '@mui/material';
import {
  Search, FileDownload, FilterList, Person, DirectionsCar,
  CheckCircle, Cancel, ArrowForward, Refresh, CalendarToday,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

const VehicleAssignmentReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(''); // '', 'assigned', 'returned'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  const fetchData = async () => {
    setLoading(true);
    const res = await InvokeApi(Config.base_url + apiList.vehicleAssignmentReport, {
      page: page + 1,
      per_page: rowsPerPage,
      search,
      status,
      start_date: startDate,
      end_date: endDate,
    });
    setLoading(false);
    if (res?.code === '200') {
      setData(res.assignments || []);
      setTotal(res.total || 0);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchData();
  };

  const handleReset = () => {
    setSearch('');
    setStatus('');
    setStartDate('');
    setEndDate('');
    setPage(0);
    setTimeout(fetchData, 100);
  };

  const exportToCSV = () => {
    const headers = ['Vehicle', 'Model', 'User', 'Phone', 'Assigned Date', 'Assigned By', 'Returned Date', 'Collected By', 'Days Used', 'Status', 'Notes'];
    const rows = data.map(row => [
      row.vehicle_number,
      row.model,
      row.user_name,
      row.user_phone,
      `${row.assigned_date} ${row.assigned_time}`,
      row.assigned_by || '',
      row.returned_date ? `${row.returned_date} ${row.returned_time}` : 'Active',
      row.collected_by || '',
      row.days_used || 0,
      row.returned_date ? 'Returned' : 'Assigned',
      row.return_notes || '',
    ]);

    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vehicle-assignment-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">
            Vehicle Assignment Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track vehicle assignments, reliefs, and user history
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<FileDownload />}
          onClick={exportToCSV}
          disabled={data.length === 0}
          sx={{ background: 'linear-gradient(135deg,#2e7d32,#388e3c)', fontWeight: 600 }}
        >
          Export CSV
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterList sx={{ color: '#1a237e' }} />
            <Typography variant="h6" fontWeight={700} color="#1a237e">Filters</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Vehicle, User, Phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                select
                size="small"
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="assigned">Currently Assigned</MenuItem>
                <MenuItem value="returned">Returned</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={1.5}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                sx={{ height: 40, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 600 }}
              >
                Search
              </Button>
            </Grid>

            <Grid item xs={12} sm={6} md={1.5}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleReset}
                sx={{ height: 40, borderColor: '#1a237e', color: '#1a237e', fontWeight: 600 }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          Showing {data.length} of {total} assignments
        </Typography>
      </Box>

      {/* Table */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ background: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Vehicle</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Assigned</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Returned</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress sx={{ color: '#1a237e' }} />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">No assignments found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#e8eaf6', color: '#1a237e', fontSize: 12, fontWeight: 700 }}>
                          <DirectionsCar fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography fontSize={13} fontWeight={700}>{row.vehicle_number}</Typography>
                          <Typography fontSize={11} color="text.secondary">{row.model} · {row.color}</Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#e3f2fd', color: '#1565c0', fontSize: 12, fontWeight: 700 }}>
                          {row.user_name?.[0]?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography fontSize={13} fontWeight={700}>
                            {row.user_name}
                            {row.user_status == 0 && (
                              <Chip label="Inactive" size="small" sx={{ ml: 1, height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#ffebee', color: '#c62828' }} />
                            )}
                          </Typography>
                          <Typography fontSize={11} color="text.secondary">{row.user_phone}</Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography fontSize={12} fontWeight={600}>{row.assigned_date}</Typography>
                      <Typography fontSize={11} color="text.secondary">
                        {row.assigned_time} · {row.assigned_by}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {row.returned_date ? (
                        <>
                          <Typography fontSize={12} fontWeight={600}>{row.returned_date}</Typography>
                          <Typography fontSize={11} color="text.secondary">
                            {row.returned_time} · {row.collected_by}
                          </Typography>
                        </>
                      ) : (
                        <Chip label="Active" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: 11 }} />
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography fontSize={13} fontWeight={700}>{row.days_used || 0} days</Typography>
                    </TableCell>

                    <TableCell>
                      {row.returned_date ? (
                        <Chip
                          icon={<CheckCircle sx={{ fontSize: 14 }} />}
                          label="Returned"
                          size="small"
                          sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 700, fontSize: 11 }}
                        />
                      ) : (
                        <Chip
                          icon={<Cancel sx={{ fontSize: 14 }} />}
                          label="Assigned"
                          size="small"
                          sx={{ bgcolor: '#e3f2fd', color: '#0277bd', fontWeight: 700, fontSize: 11 }}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View User">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/user/${row.user_id}`)}
                            sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}
                          >
                            <Person sx={{ fontSize: 16, color: '#1565c0' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Vehicle">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/vehicle/${row.vehicle_id}`)}
                            sx={{ bgcolor: '#e8eaf6', '&:hover': { bgcolor: '#c5cae9' } }}
                          >
                            <DirectionsCar sx={{ fontSize: 16, color: '#1a237e' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Card>
    </Box>
  );
};

export default VehicleAssignmentReport;
